import { useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import ResourceAsyncSelector from 'components/ResourceAsyncSelector';
import MonthPicker from 'components/MonthPicker';
import { IFilterField } from 'models/types';
import { get } from 'lodash';
import { date2str } from 'utils/date';

const PREFIX = 'FilterPanel';

const classes = {
  root: `${PREFIX}-root`,
  toolbar: `${PREFIX}-toolbar`,
  contentContainer: `${PREFIX}-contentContainer`,
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  box: `${PREFIX}-box`,
  iconButton: `${PREFIX}-iconButton`,
  divider: `${PREFIX}-divider`,
  loadingBox: `${PREFIX}-loadingBox`,
  resultContainer: `${PREFIX}-resultContainer`
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  [`&.${classes.root}`]: {
    position: 'relative',
    width: '100%',
  },

  [`& .${classes.toolbar}`]: {
    display: 'flex',
    '& > *': {
      flex: 1
    },
  },

  [`& .${classes.contentContainer}`]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },

  [`& .${classes.content}`]: {
    flex: 1,
    textAlign: 'left',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    overflowX: 'hidden',
  },

  [`& .${classes.title}`]: {
    fontSize: 40,
    margin: 0,
    marginBottom: 30,
    lineHeight: 1
  },

  [`& .${classes.box}`]: {
    padding: 1,
    display: 'flex',
    alignItems: 'center',
    minWidth: 800,
    width: '100%',
    flexWrap: 'wrap',
    '& > div': {
      minWidth: 150,
      marginRight: 15,
      marginBottom: 15,
    },
    '& > button': {
      marginRight: 15,
      marginBottom: 15,
    }
  },

  [`& .${classes.iconButton}`]: {
    padding: 0,
    height: 56,
    width: 140,
    fontSize: 18
  },

  [`& .${classes.divider}`]: {
    height: 28,
    margin: 4,
  },
  [`& .${classes.loadingBox}`]: {
    padding: theme.spacing(2),
    width: '100%',
  },

  [`& .${classes.resultContainer}`]: {
    marginTop: 20,
    marginBottom: 20,
    '& .pagination': {
      marginBottom:  110,
    }
  },
}));

interface Props {
  searchText?: string;
  fields: IFilterField[];
  filtered: boolean;
  filters: { [key: string]: string };
  onChangeFilter: (value: string, name: string) => void;
  onSearch: () => Promise<void>;
  onReset: () => Promise<void>;
}

// interface ICountryOption {
//   label: string;
//   alpha3: string;
// }
export default function FilterPanel(props: Props): JSX.Element {
  const [loading, setLoading] = useState(false);
  const {
    searchText,
    fields,
    filtered,
    filters,
    onChangeFilter,
    onSearch,
    onReset,
  } = props;

  const disabled = loading;

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await onSearch();
    setLoading(false);
  };

  const handleReset = async () => {
    setLoading(true);
    await onReset();
    setLoading(false);
  };

  const handleChange = (value: string, name: string) => {
    onChangeFilter(value, name);
  };

  const renderField = (field: IFilterField, index: number) => {
    const { label, name, widget, options } = field;
    const value = get(filters, name) || '';
    switch(widget) {
      case 'text': {
        return (
          <div key={index}>
            <TextField
              value={value}
              label={label}
              name={name}
              disabled={disabled}
              onChange={(event) => handleChange(event.target.value, name)}
              sx={{ minWidth: 200 }}
            />
          </div>
        );
      }
      case 'select': {
        return (
          <div key={index}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">{label}</InputLabel>
              <Select
                name={name}
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={value}
                label={label}
                onChange={(event) => handleChange(event.target.value, name)}
                sx={{ minWidth: 200 }}
              >
                {(options || []).map(op => <MenuItem key={op.alias} value={op.alias}>{op.label}</MenuItem>)}
              </Select>
            </FormControl>
          </div>
        );
      }
      case 'monthPicker': {
        return (
          <div key={index}>
            <MonthPicker
              value={value || null}
              label={label}
              name={name}
              disabled={disabled}
              onChange={(date: Date) => handleChange(date2str(date, 'YYYY-MM'), name)}
            />
          </div>
        );
      }
      case 'datePicker': {
        return (
          <div key={index}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                inputFormat="yyyy-MM-dd"
                label={label}
                value={value || null}
                onChange={(date: Date | null) => handleChange(date2str(date, 'YYYY-MM-DD'), name)}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </div>
        );
      }
      case 'userSelect': {
        return (
          <div key={index}>
            <ResourceAsyncSelector
              value={value}
              label={label}
              name={name}
              disabled={disabled}
              onSelect={(option) => handleChange(option.id, name)}
              resourceName="user"
            // variant="outlined"
            />
          </div>
        );
      }
      case 'organisationSelect': {
        return (
          <div key={index}>
            <ResourceAsyncSelector
              value={value}
              label={label}
              name={name}
              disabled={disabled}
              onSelect={(option) => handleChange(option.id, name)}
              resourceName="organisation"
            // variant="outlined"
            />
          </div>
        );
      }
    }
  };

  return (
    <Root className={classes.root}>
      <main className={classes.contentContainer}>
        <div className={classes.content}>
          <Box
            component="form"
            className={classes.box}
            onSubmit={handleSearch}
          >
            {fields.map(renderField)}
            <Button
              type="submit" // bind to Enter key
              disabled={disabled}
              onClick={handleSearch}
              className={classes.iconButton}
              aria-label="search"
              variant="contained"
              color="primary"
              endIcon={<SearchIcon />}
            >
              {searchText || 'Search'}
            </Button>
            {filtered && (
              <Button
                disabled={disabled}
                onClick={handleReset}
                className={classes.iconButton}
                aria-label="Reset"
                variant="outlined"
                color="primary"
                endIcon={<ClearIcon />}
              >
                Reset
              </Button>
            )}
          </Box>
        </div>
      </main>
    </Root>
  );
}
